import React, { useCallback, useEffect, useState, Suspense } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Close,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  PictureAsPdf,
  ExpandMore,
  Compare,
  CheckCircleOutlineOutlined,
  PublishedWithChanges,
  AssignmentOutlined
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { Project, ZoneDifferences, Photos } from '../types';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { captureException } from '../utils/captureException';

// Lazy load PhotoGallery to reduce initial bundle size
const PhotoGallery = React.lazy(() => import('./PhotoGallery'));

interface SeriesProps extends WithDeviceProps {
  project: Project;
  openSeries: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  onOpenPdf: () => void;
  isSmallScreen: boolean;
  setPdfType: (pdfType: string) => void;
  handleCloseAll: () => void;
  refetch: () => Promise<void> | void;
  setProject: (project: Project) => void;
}

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Series: React.FC<SeriesProps> = ({
  project,
  openSeries,
  handleClose,
  handleNextStep,
  onOpenPdf,
  isSmallScreen,
  setPdfType,
  handleCloseAll,
  refetch,
  setProject,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [referenceProjectCode, setReferenceProjectCode] = useState<string>(project?.reference_project_code || '');
  const [savingReference, setSavingReference] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState<Photos[]>([]);
  const [generating, setGenerating] = useState(false);

  // Dummy data for zone differences - template structure (overridden by backend response)
  const [zoneDifferences, setZoneDifferences] = useState<ZoneDifferences>({});

  const onCurrentElementImageClick = useCallback((e: React.MouseEvent, zone: string) => {
    e.stopPropagation();
    const zoneDiff = zoneDifferences[zone];
    if (!zoneDiff) return;
    
    // Collect photos for comparison (reference and current)
    const photos: Photos[] = [];
    if (zoneDiff.reference_photo) {
      photos.push(zoneDiff.reference_photo);
    }
    if (zoneDiff.current_photo) {
      photos.push(zoneDiff.current_photo);
    }
    
    if (photos.length > 0) {
      setGalleryPhotos(photos);
      setGalleryOpen(true);
    }
  }, [zoneDifferences]);

  const handleGenerate = async () => {
    if (!project?.project_code || !user) {
      setError(t('errors.processing.userNotAuthenticated'));
      return;
    }

    if (!project?.project_code || !project?.series_differences) {
      setError(t('noDataToSave'));
      return;
    }

    const idToken = await user.getIdToken();
    if (!idToken) {
      setError(t('errors.processing.noIdToken', 'No se encontró token de autenticación'));
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      // Calculate version
      const version = (project.series_revision || 0) + 1;

      
      // Generate PDF
      const seriesPdfResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/generate/series-pdf`, 
        {
          project_code: project.project_code,
          version: version,
        }, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      // Update project status
      const updateData = {
        status_series: 'completed',
        series_verified_by: user?.uid,
        series_revision: version,
      };
      console.log('update: ', updateData)
      await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, 
        updateData, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      console.log('✅ Series PDF generated successfully:', seriesPdfResponse);
      
      refetch();
    } catch (error: any) {
      captureException(error, { tags: { feature: 'series' }, extra: { action: 'generateSeriesPdf', project_code: project?.project_code } });
      console.error('❌ Error generating series PDF:', error);
      setError(error.response?.data?.error || t('capture.updateFailed'));
    } finally {
      setGenerating(false);
    }
  }

  useEffect(() => {
    const checkSeries = async () => {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        setError(t('capture.noTokenFound'));
        return;
      }
      
      axios.put(`${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, {
        status_series: "completed",
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      }).then(() => {
        setProject({
          ...project,
          status_series: "completed",
        });
      }).catch((error) => {
        captureException(error, { tags: { feature: 'series' }, extra: { action: 'checkSeries', project_code: project?.project_code } });
        setError(error.response.data.error || t('capture.updateFailed'));
      });
    };

    if (project && project.status_series !== "completed") {
      if (project.serial_code === undefined || project.serial_code === null || project.serial_code === '' || project.serial_code.split(',').length === 0 || project.series_differences !== undefined) {
        checkSeries();
      }
    }
  }, [project]);

  useEffect(() => {
    setReferenceProjectCode(project?.reference_project_code || '');
  }, [project?.project_code, project?.reference_project_code]);

  // Load zoneDifferences from project data when component loads or project changes
  useEffect(() => {
    if (!project?.series_differences) {
      setZoneDifferences({});
      return;
    }

    setZoneDifferences(project?.series_differences);
  }, [project?.series_differences, project?.fotos, project?.project_code]);

  const handleSaveAndAnalyse = async () => {
    const projectCode = project?.project_code;
    const trimmedReference = (referenceProjectCode || '').trim();

    if (!projectCode) return;
    if (!trimmedReference) {
      setError('Reference project code is required');
      return;
    }

    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError(t('capture.noTokenFound'));
      return;
    }

    try {
      setSavingReference(true);
      setError(null);

      const analyseResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/analyse-series`,
        { current_project_code: projectCode, reference_project_code: trimmedReference },
        { headers: { 'Authorization': `Bearer ${idToken}` } }
      );

      const responseDiffs =
        analyseResponse?.data?.zone_differences ||
        analyseResponse?.data?.zoneDifferences ||
        analyseResponse?.data?.differences;

      await refetch();
      if (responseDiffs && typeof responseDiffs === 'object') {
        setZoneDifferences(responseDiffs);
      }

    } catch (err: any) {
      captureException(err, { tags: { feature: 'series' }, extra: { action: 'handleSaveAndAnalyse', project_code: project?.project_code } });
      setError(err?.response?.data?.error || err?.message || t('capture.updateFailed'));
    } finally {
      setSavingReference(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={openSeries && project !== null}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"series"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        <AppBar
          color="inherit"
          position="fixed"
          sx={{
            // zIndex: (theme) => theme.zIndex.drawer,
            pt: isSmallScreen ? "56px" : "64px",
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            {/* <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('inspections.seriesComparison')}</Typography> */}
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookName', 'Libro de Homologación')} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
        {project && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
              <Paper sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                  <TextField
                    label={t('series.projectReference')}
                    value={referenceProjectCode}
                    onChange={(e) => setReferenceProjectCode(e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={savingReference || !(project.editable !== false)}
                    InputProps={{
                      endAdornment: (project.editable !== false) ? (
                        <InputAdornment position="end">
                          <Tooltip title={t('common.save')} placement="bottom">
                            <IconButton
                              onClick={handleSaveAndAnalyse}
                              disabled={savingReference || !referenceProjectCode.trim()}
                              edge="end"
                              color="primary"
                              sx={{ p: "14px"}}
                            >
                              {savingReference ? (
                                <CircularProgress size={20} color="primary" />
                              ) : (
                                <PublishedWithChanges />
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                  />
                </Box>
              </Paper>
              {/* Zone Accordions */}
              {referenceProjectCode === project?.project_code ? (
                <Paper sx={{ borderRadius: '2px', overflow: 'hidden', bgcolor: 'info.light' }}>
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CheckCircleOutlineOutlined sx={{ color: 'info.main', fontSize: 42, mb: 0.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 1 }}>
                      {t('series.referenceProject')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'info.dark' }}>
                      {t('series.referenceProjectMessage')}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                Object.entries(zoneDifferences)
                .sort(([zoneA], [zoneB]) => {
                  const zoneOrder: { [key: string]: number } = {
                    'Delantera': 1,
                    'Derecha': 2,
                    'Trasera': 3,
                    'Izquierda': 4,
                  };
                  const orderA = zoneOrder[zoneA] ?? 999;
                  const orderB = zoneOrder[zoneB] ?? 999;
                  return orderA - orderB;
                })
                .map(([zone, zoneDiff]) => (
                zoneDiff.differences.length > 0 ? (
                  <Accordion
                    key={zone}
                    sx={{
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                      '& .MuiAccordionDetails-root': { paddingTop: 0 },
                      '& .MuiAccordionSummary-root': { margin: 0 },
                      borderRadius: '8px !important',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ExpandMore />
                        </Box>
                      }
                      aria-controls={`panel-${zone}-content`}
                      id={`panel-${zone}-header`}
                      sx={{
                        '&.Mui-expanded': { margin: 0 },
                        '& .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: '0px !important' },
                        '&.Mui-expanded .MuiAccordionSummary-content': { margin: '12px 0 !important', mb: '0px !important' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2, fontSize: 18 }}>
                          {zone}
                        </Typography>
                        {zoneDiff.current_photo && zoneDiff.reference_photo && (
                        <Box sx={{ display: 'flex', gap: 0.5, mb: '12px' }}>
                          <Tooltip title="Ver comparación" placement="bottom">
                            <IconButton
                              onClick={(e) => onCurrentElementImageClick(e, zone)}
                              size="small"
                              sx={{
                                mr: 1,
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                            >
                              <Compare/>
                            </IconButton>
                          </Tooltip>
                        </Box>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mt: 2 }}>
                        <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 0, pb: 0, pt: 0 }}>
                          {zoneDiff.differences.map((element, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                borderBottom: index < zoneDiff.differences.length - 1 ? '1px solid #E0E0E0' : 'none',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                minHeight: 56,
                              }}
                            >
                              <ListItemText 
                                primary={element}
                                sx={{ mr: 3 }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Paper
                    key={zone}
                    sx={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 1.5, pr: "16px" }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 18 }}>
                          {zone}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Ver comparación" placement="bottom">
                          <IconButton
                            onClick={(e) => onCurrentElementImageClick(e, zone)}
                            size="small"
                            sx={{
                              mr: "4px",
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                          >
                            <Compare/>
                          </IconButton>
                        </Tooltip>
                        <CheckCircleOutlineOutlined sx={{ color: 'success.main', mt: "5px" }} />
                      </Box>
                    </Box>
                  </Paper>
                )
              )))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {referenceProjectCode !== project?.project_code && referenceProjectCode !== '' && referenceProjectCode !== null && <Button startIcon={<DescriptionOutlined />} variant="outlined" color="primary" size="large" disabled={generating} onClick={() => handleGenerate()} >{t('finish.generatePDF')}</Button>}
              <Button variant="contained" color="primary" size="large" onClick={() => handleNextStep('series-comparison')}>{t('capture.vehicle.next')}</Button>
            </Box>
          </Box>
        )}
      </DialogTitle>

      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}

      {/* Photo Gallery for Comparison */}
      {galleryOpen && (
        <Suspense fallback={null}>
          <PhotoGallery
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            photos={galleryPhotos}
            isMobile={isSmallScreen}
            grouped={true}
          />
        </Suspense>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Series));
